import { Outlet, useLocation } from "@tanstack/react-router";
import { NavigatorProvider } from "../NavigatorContext";
import { AppHeader } from "components/Header/AppHeader";
import { TooltipProvider } from "contexts/TooltipContext";
import { Footer } from "components/Footer/Footer";
import { useTheme } from "contexts/ThemeContext";
import { useEscapeBlur } from "hooks/listeners/useEscapeBlur";
import { useEffect, useRef, useState } from "react";
import { usePageConfig } from "hooks/usePageConfig";
import { useForceScrollToTop } from "components/Common/hooks/useForceScrollToTop";
import classnames from "classnames/bind";
import styles from "./root.module.scss";
import { Helmet } from "react-helmet";
import { TooltipStyleProvider } from "contexts/TooltipStyleContext";
import { Home } from "components/Home/Home";
import { IFrameProvider } from "contexts/IframeContext";
import { ErrorBoundary } from "react-error-boundary";
import { CrashPage } from "CrashPage";
import { PopupManager } from "components/PopupManager/PopupManager";
import { isError, noop } from "lodash";
import { useAuth } from "contexts/AuthContext";
import html2canvas from "html2canvas";
import { useKeydownListener } from "hooks/listeners/useKeydownListener";
import { Modal } from "components/Common/Modal";
import { usePortal } from "contexts/PortalContext";
import { Image } from "components/Common/Image/Image";
import { Button } from "components/Common/Button";
import { AnalyticsProvider } from "contexts/AnalyticsContext";
import { Download, X } from "lucide-react";
import { useDatabase } from "contexts/database/DatabaseContext";
import { HelpTooltip } from "components/Common/HelpTooltip";
import { Tooltip } from "components/Common/Tooltip/Tooltip";
import { TutorialProvider } from "contexts/database/TutorialContext";
import { ScreenshotContextProvider } from "contexts/ScreenshotContext";

const classNameBuilder = classnames.bind(styles);

const RootContent = ({
  contentRef,
  screenshotPortalProps,
  setScreenshotStyle,
}) => {
  const [appClassName, contentClassName] = usePageConfig([
    "appClassName",
    "contentClassName",
  ]);
  const { shouldSetupUser } = useAuth();
  const [screenshot, setScreenshot] = useState(null);

  useEffect(() => {
    if (screenshotPortalProps.isPortalComponentRendered) {
      html2canvas(document.querySelector("#capture2image"), {
        allowTaint: true,
        useCORS: true,
        logging: false,
        height: window.outerHeight + window.innerHeight,
        windowHeight: window.outerHeight + window.innerHeight,
      })
        .then((canvas) => {
          setScreenshot(canvas.toDataURL());
        })
        .catch((e) => {
          console.error(e);
          setScreenshot(e);
        });
    }
  }, [screenshotPortalProps.isPortalComponentRendered]);

  useKeydownListener(
    (e) => {
      if (
        !screenshotPortalProps.isPortalComponentRendered &&
        e.code === "KeyP" &&
        e.ctrlKey
      ) {
        e.preventDefault();
        setScreenshot(null);

        if (e.shiftKey) {
          setScreenshotStyle("normal");
        } else {
          setScreenshotStyle("gray");
        }

        screenshotPortalProps.setIsPortalComponentRendered(true);
      }
    },
    [screenshotPortalProps.isPortalComponentRendered]
  );

  return (
    <TooltipStyleProvider scrollRef={contentRef}>
      <div
        className={classNameBuilder("scrollable", appClassName)}
        ref={contentRef}
      >
        {screenshotPortalProps.isPortalComponentRendered && screenshot && (
          <Modal title={"Screenshot"} portalProps={screenshotPortalProps}>
            {isError(screenshot) ? (
              <div>Failed to take screenshot. Please try again</div>
            ) : (
              <div className={styles["screenshot-content"]}>
                <Image
                  src={screenshot}
                  className={styles["screenshot-image"]}
                  contain
                  shouldHideBackground
                />
                <a
                  download={`${Date.now()}-wildsea-screenshot`}
                  href={screenshot}
                  title="Download Screenshot"
                >
                  <Button className={styles["screenshot-button"]}>
                    <Download /> Download
                  </Button>
                </a>
              </div>
            )}
          </Modal>
        )}
        <div className={classNameBuilder("content", contentClassName)}>
          {!shouldSetupUser && <PopupManager />}
          <TooltipProvider className={styles["tooltip-provider"]}>
            <Home>
              <Outlet />
            </Home>
          </TooltipProvider>
        </div>
        <Footer />
      </div>
    </TooltipStyleProvider>
  );
};

export const Root = () => {
  const { themeClassNames, theme } = useTheme();
  const { localMode } = useDatabase();
  useEscapeBlur();
  const location = useLocation();
  const contentRef = useRef();
  const ref = useRef();
  useForceScrollToTop({ scrollRef: contentRef, deps: [location?.pathname] });
  const screenshotPortalProps = usePortal();
  const [screenshotStyle, setScreenshotStyle] = useState("gray");

  return (
    <ScreenshotContextProvider
      screenshotStyle={
        screenshotPortalProps.isPortalComponentRendered && screenshotStyle
      }
    >
      <AnalyticsProvider>
        <NavigatorProvider>
          <TutorialProvider>
            <IFrameProvider>
              <TooltipProvider>
                <ErrorBoundary FallbackComponent={CrashPage} onReset={noop}>
                  <div
                    className={classNameBuilder("app", ...themeClassNames, {
                      screenshot:
                        screenshotPortalProps.isPortalComponentRendered,
                      "gray-screenshot":
                        screenshotPortalProps.isPortalComponentRendered &&
                        screenshotStyle === "gray",
                    })}
                    id="capture2image"
                    ref={ref}
                  >
                    <Helmet>
                      <link
                        rel="icon"
                        type="image/png"
                        href={`/wildsea-character-sheet/favicon-${theme.secondary}.ico`}
                      />
                    </Helmet>
                    <AppHeader />
                    <RootContent
                      contentRef={contentRef}
                      screenshotPortalProps={screenshotPortalProps}
                      screenshotStyle={screenshotStyle}
                      setScreenshotStyle={setScreenshotStyle}
                    />
                    {localMode && <LocalModeWarning />}
                  </div>
                </ErrorBoundary>
              </TooltipProvider>
            </IFrameProvider>
          </TutorialProvider>
        </NavigatorProvider>
      </AnalyticsProvider>
    </ScreenshotContextProvider>
  );
};

const LocalModeWarning = () => {
  const [hidden, setHidden] = useState(false);
  const { signOut } = useAuth();

  if (hidden) {
    return null;
  }

  return (
    <div className={styles["sandbox"]} onClick={() => signOut()}>
      CLICK TO EXIT DEMO{" "}
      <HelpTooltip>
        <div>
          You are in Demo Mode. All sheets created in Demo Mode are stored
          locally on your machine. No other users will be able to access
          anything you create in Demo Mode. If you'd like to interact with other
          users, you will need to create an account.
        </div>
        <br />
        <div>
          If you'd like to use a sheet you created in demo mode on a real group,
          click CTRL + S to save the sheet, then use the upload feature on the
          Groups page to add the sheet to a real group.
        </div>
      </HelpTooltip>
      <Button
        className={styles["hide-sandbox"]}
        onClick={(e) => {
          e.stopPropagation();
          setHidden(true);
        }}
        icon
        round
        hideBackground
        colorful
        disableMovement
      >
        <X />
        <Tooltip>Hide</Tooltip>
      </Button>
    </div>
  );
};
